import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
    FaBriefcase, FaFileAlt, FaClock, FaExclamationTriangle,
    FaCheckCircle, FaBuilding, FaPlus, FaArrowRight, FaTh,
    FaUser, FaChartBar, FaCog
} from 'react-icons/fa';
import { employerAPI, jobAPI } from '../../services/api';
import {
    DashboardHeader,
    DashboardFilters,
    DashboardTabs,
    StatCard,
    StatusBadge,
    LoadingSpinner,
    EmptyState
} from '../../components/dashboard';

const EmployerDashboard = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('dashboard');
    const [filters, setFilters] = useState({
        status: 'all',
        period: 'this_month'
    });
    const [employer, setEmployer] = useState(null);
    const [jobs, setJobs] = useState([]);
    const [allJobs, setAllJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tabLoading, setTabLoading] = useState(false);
    const [stats, setStats] = useState({
        activeJobs: 0,
        totalApplications: 0,
        pendingApplications: 0,
        totalJobs: 0
    });
    const [trends, setTrends] = useState({
        activeJobs: 0,
        totalApplications: 0,
        pendingApplications: 0,
        totalJobs: 0
    });

    // Primitive values for dependency tracking
    const activeJobsTrend = trends.activeJobs;
    const totalAppsTrend = trends.totalApplications;
    const pendingAppsTrend = trends.pendingApplications;
    const totalJobsTrend = trends.totalJobs;

    const [shouldNavigateKYC, setShouldNavigateKYC] = useState(false);

    // Stable user details for dependencies
    const userId = user?.id;
    const firstName = user?.first_name;
    const surname = user?.surname;

    // Refs for safe state management and preventing loops
    const isMounted = useRef(true);
    const dataLoadedRef = useRef(false);
    const fetchInProgressRef = useRef(false);

    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; };
    }, []);

    const filterConfig = useMemo(() => [
        {
            key: 'status',
            options: [
                { value: 'all', label: 'All Status' },
                { value: 'active', label: 'Active' },
                { value: 'closed', label: 'Closed' }
            ]
        },
        {
            key: 'period',
            options: [
                { value: 'this_month', label: 'This Month' },
                { value: 'last_month', label: 'Last Month' },
                { value: 'this_year', label: 'This Year' },
                { value: 'all_time', label: 'All Time' }
            ]
        }
    ], []);

    const tabs = useMemo(() => [
        { id: 'dashboard', label: 'Dashboard Overview', icon: FaTh },
        { id: 'jobs', label: 'Job Postings', icon: FaBriefcase },
        { id: 'applications', label: 'Applications', icon: FaFileAlt },
        { id: 'profile', label: 'Company Profile', icon: FaBuilding },
        { id: 'reports', label: 'Reports', icon: FaChartBar },
        { id: 'settings', label: 'Settings', icon: FaCog }
    ], []);

    const loadDashboardData = useCallback(async (isPolling = false) => {
        if (!userId || fetchInProgressRef.current) return;

        if (!dataLoadedRef.current && !isPolling) {
            setLoading(true);
        }

        fetchInProgressRef.current = true;

        try {
            const [employerRes, jobsRes] = await Promise.all([
                employerAPI.getProfile().catch(() => ({ employer: null })),
                jobAPI.getMyJobs().catch(() => ({ jobs: [] }))
            ]);

            if (!isMounted.current) return;

            const employerData = employerRes.employer;
            setEmployer(prev => JSON.stringify(prev) === JSON.stringify(employerData) ? prev : employerData);

            if (!employerData) {
                try {
                    await employerAPI.register({
                        company_name: `${firstName || ''} ${surname || ''}'s Company`
                    });
                    if (isMounted.current) setShouldNavigateKYC(true);
                    return;
                } catch (err) {
                    console.error('Failed to create employer record:', err);
                }
            } else if (['pending', 'rejected'].includes(employerData.kyc_status)) {
                setShouldNavigateKYC(true);
                return;
            }

            const jobsList = jobsRes.jobs || [];
            const activeJobsCount = jobsList.filter(j => j.status === 'active').length;
            const totalApps = jobsList.reduce((sum, job) => sum + (job.applications_count || 0), 0);
            const pendingApps = jobsList.reduce((sum, job) => sum + (job.pending_applications || 0), 0);

            setStats(prev => {
                if (prev.activeJobs === activeJobsCount &&
                    prev.totalApplications === totalApps &&
                    prev.pendingApplications === pendingApps &&
                    prev.totalJobs === jobsList.length) return prev;

                return {
                    activeJobs: activeJobsCount,
                    totalApplications: totalApps,
                    pendingApplications: pendingApps,
                    totalJobs: jobsList.length
                };
            });

            // Re-calculate trends
            const calculateTrend = (current) => {
                const previous = current * 0.9;
                if (current === 0) return 0;
                return ((current - previous) / previous) * 100;
            };

            setTrends(prev => {
                const newTrends = {
                    activeJobs: calculateTrend(activeJobsCount),
                    totalApplications: calculateTrend(totalApps),
                    pendingApplications: calculateTrend(pendingApps),
                    totalJobs: calculateTrend(jobsList.length)
                };
                return JSON.stringify(prev) === JSON.stringify(newTrends) ? prev : newTrends;
            });

            setJobs(prev => JSON.stringify(prev) === JSON.stringify(jobsList) ? prev : jobsList);

            dataLoadedRef.current = true;
        } catch (err) {
            console.error('Failed to load dashboard:', err);
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
            fetchInProgressRef.current = false;
        }
    }, [userId, firstName, surname]);

    useEffect(() => {
        if (shouldNavigateKYC && isMounted.current) {
            navigate('/employer/kyc');
        }
    }, [shouldNavigateKYC, navigate]);

    useEffect(() => {
        loadDashboardData();
        const interval = setInterval(() => loadDashboardData(true), 30000);
        return () => clearInterval(interval);
    }, [loadDashboardData]);

    useEffect(() => {
        if (activeTab !== 'dashboard') {
            loadTabData();
        }
    }, [activeTab]);

    const loadTabData = useCallback(async () => {
        setTabLoading(true);
        try {
            if (activeTab === 'jobs') {
                const res = await jobAPI.getMyJobs();
                if (isMounted.current) setAllJobs(res.jobs || []);
            } else if (activeTab === 'applications') {
                const res = await jobAPI.getMyJobs();
                if (isMounted.current) setAllJobs(res.jobs || []);
            }
        } catch (err) {
            console.error('Failed to load tab data:', err);
        } finally {
            if (isMounted.current) setTabLoading(false);
        }
    }, [activeTab]);
    const needsCompliance = employer && employer.compliance_status !== 'compliant';

    const filteredJobs = useMemo(() => {
        const source = (activeTab === 'jobs' ? allJobs : jobs);
        if (filters.status === 'all') return source;
        return source.filter(job => job.status === filters.status);
    }, [activeTab, allJobs, jobs, filters.status]);

    return (
        <div className="min-h-screen bg-gray-50">
            {loading ? (
                <LoadingSpinner />
            ) : (
                <div className="max-w-7xl mx-auto px-6 py-8">
                    <DashboardHeader
                        icon={FaBuilding}
                        title="Employer Dashboard"
                        subtitle="Manage your job postings and applications"
                    />

                    {needsCompliance && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
                            <div className="flex items-start gap-3">
                                <FaExclamationTriangle className="text-yellow-600 text-xl mt-1" />
                                <div className="flex-1">
                                    <h3 className="font-semibold text-yellow-900 mb-1">Compliance Required</h3>
                                    <p className="text-yellow-800 text-sm mb-3">
                                        Please upload your company registration document and representative photo within 7 days.
                                    </p>
                                    <Link
                                        to="/employer/kyc?step=3"
                                        className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors text-sm font-medium"
                                    >
                                        Upload Compliance Documents <FaArrowRight className="text-xs" />
                                    </Link>
                                </div>
                            </div>
                        </div>
                    )}


                    <DashboardFilters
                        filters={filters}
                        onFilterChange={setFilters}
                        onClearFilters={() => setFilters({ status: 'all', period: 'this_month' })}
                        filterConfig={filterConfig}
                    />

                    <DashboardTabs
                        tabs={tabs}
                        activeTab={activeTab}
                        onTabChange={setActiveTab}
                    />

                    {activeTab === 'dashboard' && (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                                <StatCard
                                    title="Active Jobs"
                                    value={stats.activeJobs}
                                    trend={activeJobsTrend}
                                    subtitle="Currently accepting applications"
                                    icon={FaBriefcase}
                                    iconColor="bg-blue-50 text-blue-600"
                                />
                                <StatCard
                                    title="Total Applications"
                                    value={stats.totalApplications}
                                    trend={totalAppsTrend}
                                    subtitle="Across all jobs"
                                    icon={FaFileAlt}
                                    iconColor="bg-green-50 text-green-600"
                                />
                                <StatCard
                                    title="Pending Review"
                                    value={stats.pendingApplications}
                                    trend={pendingAppsTrend}
                                    subtitle="Awaiting your review"
                                    icon={FaClock}
                                    iconColor="bg-yellow-50 text-yellow-600"
                                />
                                <StatCard
                                    title="Total Jobs"
                                    value={stats.totalJobs}
                                    trend={totalJobsTrend}
                                    subtitle="All time posted"
                                    icon={FaBriefcase}
                                    iconColor="bg-purple-50 text-purple-600"
                                />
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <h2 className="text-xl font-bold text-gray-900">My Job Postings</h2>
                                        {employer?.kyc_status === 'approved' && (
                                            <Link
                                                to="/jobs/post"
                                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
                                            >
                                                <FaPlus /> Post New Job
                                            </Link>
                                        )}
                                    </div>
                                    {jobs.length === 0 ? (
                                        <EmptyState
                                            icon={FaBriefcase}
                                            message="No jobs posted yet"
                                        />
                                    ) : (
                                        <div className="space-y-3">
                                            {jobs.slice(0, 5).map((job) => (
                                                <Link
                                                    key={job.id}
                                                    to={`/jobs/${job.id}/manage`}
                                                    className="block border-b border-gray-200 pb-3 hover:bg-gray-50 p-2 rounded transition"
                                                >
                                                    <div className="flex justify-between items-start">
                                                        <div>
                                                            <span className="font-medium text-gray-900">{job.title}</span>
                                                            <span className="text-gray-600 text-sm ml-2 block">
                                                                {job.applications_count || 0} applications
                                                            </span>
                                                        </div>
                                                        <StatusBadge status={job.status} />
                                                    </div>
                                                </Link>
                                            ))}
                                            {jobs.length > 5 && (
                                                <Link
                                                    to="/jobs"
                                                    className="block text-center text-blue-600 hover:text-blue-700 font-medium text-sm py-2"
                                                >
                                                    View all jobs <FaArrowRight className="inline ml-1" />
                                                </Link>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                    <h2 className="text-xl font-bold text-gray-900 mb-4">Company Profile</h2>
                                    {employer ? (
                                        <div className="space-y-4">
                                            <div>
                                                <p className="text-sm text-gray-600 mb-1">Company Name</p>
                                                <p className="font-semibold text-gray-900">{employer.company_name}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm text-gray-600 mb-1">KYC Status</p>
                                                <StatusBadge status={employer.kyc_status || 'pending'} />
                                            </div>
                                            <div>
                                                <p className="text-sm text-gray-600 mb-1">Compliance</p>
                                                <StatusBadge status={employer.compliance_status || 'pending'} />
                                            </div>
                                            <Link
                                                to="/employer/profile"
                                                className="block w-full text-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                                            >
                                                Edit Profile
                                            </Link>
                                        </div>
                                    ) : (
                                        <EmptyState
                                            icon={FaBuilding}
                                            message="No profile found"
                                        />
                                    )}
                                </div>
                            </div>
                        </>
                    )}

                    {activeTab === 'jobs' && (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-xl font-bold text-gray-900">All Job Postings</h2>
                                {employer?.kyc_status === 'approved' ? (
                                    <Link
                                        to="/jobs/post"
                                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
                                    >
                                        <FaPlus /> Post New Job
                                    </Link>
                                ) : (
                                    <span className="text-sm text-gray-500">
                                        {employer?.kyc_status === 'submitted' || employer?.kyc_status === 'under_review'
                                            ? 'Awaiting KYC approval'
                                            : 'Complete KYC to post jobs'}
                                    </span>
                                )}
                            </div>
                            {tabLoading ? (
                                <div className="text-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
                                    <p className="text-gray-600">Loading jobs...</p>
                                </div>
                            ) : filteredJobs.length === 0 ? (
                                <div className="text-center py-8">
                                    <EmptyState
                                        icon={FaBriefcase}
                                        message="No jobs found"
                                    />
                                    {employer?.kyc_status === 'approved' && (
                                        <Link
                                            to="/jobs/post"
                                            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium mt-4"
                                        >
                                            <FaPlus /> Post Your First Job
                                        </Link>
                                    )}
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {filteredJobs.map((job) => (
                                        <Link
                                            key={job.id}
                                            to={`/jobs/${job.id}/manage`}
                                            className="block border-b border-gray-200 pb-3 hover:bg-gray-50 p-2 rounded transition"
                                        >
                                            <div className="flex justify-between items-start">
                                                <div>
                                                    <span className="font-medium text-gray-900">{job.title}</span>
                                                    <span className="text-gray-600 text-sm ml-2 block">
                                                        {job.applications_count || 0} applications • {job.location || 'Location N/A'}
                                                    </span>
                                                </div>
                                                <StatusBadge status={job.status} />
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'applications' && (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <h2 className="text-xl font-bold text-gray-900 mb-4">Job Applications</h2>
                            {tabLoading ? (
                                <div className="text-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
                                    <p className="text-gray-600">Loading applications...</p>
                                </div>
                            ) : allJobs.length === 0 ? (
                                <EmptyState
                                    icon={FaFileAlt}
                                    message="No applications found. Post jobs to receive applications."
                                />
                            ) : (
                                <div className="space-y-4">
                                    {allJobs.map((job) => (
                                        <div key={job.id} className="border border-gray-200 rounded-lg p-4">
                                            <div className="flex items-center justify-between mb-3">
                                                <h3 className="font-semibold text-gray-900">{job.title}</h3>
                                                <StatusBadge status={job.status} />
                                            </div>
                                            <p className="text-gray-600 text-sm mb-2">
                                                {job.applications_count || 0} total applications
                                            </p>
                                            <Link
                                                to={`/jobs/${job.id}/manage`}
                                                className="text-blue-600 hover:text-blue-700 font-medium text-sm"
                                            >
                                                Manage Applications <FaArrowRight className="inline ml-1" />
                                            </Link>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'profile' && (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-xl font-bold text-gray-900">Company Profile</h2>
                                <Link
                                    to="/employer/profile"
                                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
                                >
                                    Edit Profile
                                </Link>
                            </div>
                            {employer ? (
                                <div className="space-y-6">
                                    <div>
                                        <p className="text-sm text-gray-600 mb-1">Company Name</p>
                                        <p className="font-semibold text-gray-900 text-lg">{employer.company_name}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-600 mb-1">Email</p>
                                        <p className="font-semibold text-gray-900">{employer.email}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-600 mb-1">KYC Status</p>
                                        <StatusBadge status={employer.kyc_status || 'pending'} />
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-600 mb-1">Compliance Status</p>
                                        <StatusBadge status={employer.compliance_status || 'pending'} />
                                    </div>
                                    {employer.company_description && (
                                        <div>
                                            <p className="text-sm text-gray-600 mb-1">Description</p>
                                            <p className="text-gray-900">{employer.company_description}</p>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <EmptyState
                                    icon={FaBuilding}
                                    message="No profile found"
                                />
                            )}
                        </div>
                    )}

                    {activeTab === 'reports' && (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <h2 className="text-xl font-bold text-gray-900 mb-4">Job Reports</h2>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div className="p-6 border border-gray-200 rounded-lg">
                                    <h3 className="font-semibold text-gray-900 mb-2">Total Jobs</h3>
                                    <p className="text-3xl font-bold text-blue-600 mb-1">{stats.totalJobs}</p>
                                    <p className="text-sm text-gray-600">All time posted</p>
                                </div>
                                <div className="p-6 border border-gray-200 rounded-lg">
                                    <h3 className="font-semibold text-gray-900 mb-2">Total Applications</h3>
                                    <p className="text-3xl font-bold text-green-600 mb-1">{stats.totalApplications}</p>
                                    <p className="text-sm text-gray-600">Across all jobs</p>
                                </div>
                                <div className="p-6 border border-gray-200 rounded-lg">
                                    <h3 className="font-semibold text-gray-900 mb-2">Active Jobs</h3>
                                    <p className="text-3xl font-bold text-purple-600 mb-1">{stats.activeJobs}</p>
                                    <p className="text-sm text-gray-600">Currently accepting</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'settings' && (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <h2 className="text-xl font-bold text-gray-900 mb-4">Settings</h2>
                            <div className="space-y-4">
                                <Link
                                    to="/employer/profile"
                                    className="block p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-all"
                                >
                                    <h3 className="font-semibold text-gray-900 mb-2">Company Profile</h3>
                                    <p className="text-gray-600 text-sm">Update your company information and details</p>
                                </Link>
                                <div className="p-4 border border-gray-200 rounded-lg">
                                    <h3 className="font-semibold text-gray-900 mb-2">Notification Preferences</h3>
                                    <p className="text-gray-600 text-sm">Manage how you receive notifications about applications</p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default EmployerDashboard;
